import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, History, Loader2, Plus, Share2 } from "lucide-react";
import { API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { useSubjects } from "../SubjectContext";
import { subscribeCasesChanged } from "../lib/caseListRefresh";
import { Sidebar } from "./QueryPage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import SummaryCard from "../components/history/SummaryCard";
import RecordTimeline from "../components/history/RecordTimeline";
import AddRecordModal, { HistoryConsentDialog } from "../components/history/AddRecordModal";
import RecordDetail from "../components/history/RecordDetail";
import DoctorPacketDialog from "../components/history/DoctorPacketDialog";
import ManageShares from "../components/history/ManageShares";

const HistoryPage = () => {
  const { t } = useLanguage();
  const tRef = useRef(t);
  tRef.current = t;
  const { subjects, activeSubjectId, setActiveSubjectId } = useSubjects();
  const [subjectFilter, setSubjectFilter] = useState(() => activeSubjectId || "");
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [oneOff, setOneOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailSubjectId, setDetailSubjectId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [packetTab, setPacketTab] = useState(null);

  useEffect(() => {
    if (activeSubjectId && !subjectFilter) setSubjectFilter(activeSubjectId);
  }, [activeSubjectId, subjectFilter]);

  const personId = subjectFilter && subjectFilter !== "one_off" ? subjectFilter : null;
  const activePerson = (Array.isArray(subjects) ? subjects : []).find(
    (item) => item.subject_id === personId,
  );
  const consentCurrent = Boolean(summary?.history_consent_current);

  const load = useCallback(async () => {
    if (!subjectFilter) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (subjectFilter === "one_off") {
        const response = await axios.get(`${API}/council/history`, {
          params: { subject_mode: "one_off", limit: 50 },
          headers: { "Cache-Control": "no-cache" },
        });
        setOneOff(response.data?.data?.items || []);
        setRecords([]);
        setRecordsTotal(0);
        setSummary(null);
        return;
      }
      const [summaryRes, recordsRes] = await Promise.all([
        axios.get(`${API}/subjects/${subjectFilter}/summary`),
        axios.get(`${API}/subjects/${subjectFilter}/records`, { params: { limit: 100 } }),
      ]);
      setSummary(summaryRes.data?.data || null);
      setRecords(recordsRes.data?.data?.items || []);
      setRecordsTotal(Number(recordsRes.data?.data?.total) || 0);
      setOneOff([]);
    } catch {
      toast.error(tRef.current("history.loadError"));
    } finally {
      setLoading(false);
    }
  }, [subjectFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => subscribeCasesChanged(() => {
    load();
  }), [load]);

  const openAdd = () => {
    if (!consentCurrent) {
      setConsentOpen(true);
      return;
    }
    setAddOpen(true);
  };

  return (
    <div className="flex h-screen bg-background" data-testid="history-page">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-14 md:h-16 border-b border-border flex items-center justify-between px-4 md:px-6 pl-14 md:pl-6 gap-2 bg-background">
          <div className="flex items-center gap-2 min-w-0">
            <History className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <h1 className="text-base md:text-lg font-semibold truncate">{t("history.title")}</h1>
            {personId && summary ? (
              <Badge variant="secondary" className="text-[10px]">
                {summary.record_count || 0}
              </Badge>
            ) : null}
          </div>
          {personId && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPacketTab("link")}
                data-testid="share-history-btn"
                className="gap-1"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t("history.shareWithDoctor")}</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPacketTab("download")}
                data-testid="export-history-btn"
                className="gap-1"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t("history.downloadPacket")}</span>
              </Button>
              <Button size="sm" onClick={openAdd} data-testid="add-record-btn" className="gap-1">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">{t("history.addRecord")}</span>
              </Button>
            </div>
          )}
        </header>

        <div className="border-b border-border p-3 md:p-4">
          <select
            data-testid="history-subject-filter"
            className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            value={subjectFilter}
            onChange={(e) => {
              const value = e.target.value;
              setSubjectFilter(value);
              if (value && value !== "one_off") setActiveSubjectId(value);
            }}
          >
            {(Array.isArray(subjects) ? subjects : []).map((s) => (
              <option key={s.subject_id} value={s.subject_id}>
                {s.display_name}
              </option>
            ))}
            <option value="one_off">{t("subjects.filterOneOff")}</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 max-w-3xl w-full space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : subjectFilter === "one_off" ? (
            <div data-testid="one-off-cases">
              <h2 className="text-sm font-medium mb-2">{t("history.oneOffCases")}</h2>
              {oneOff.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("history.noOneOff")}</p>
              ) : (
                <ul className="divide-y divide-border">
                  {oneOff.map((item) => (
                    <li key={item.query_id} className="py-2 text-sm">
                      <span className="text-xs text-muted-foreground block">
                        {(item.created_at || "").slice(0, 10)}
                      </span>
                      {item.case_summary || item.query_text || t("history.aiSecondOpinion")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <>
              <SummaryCard summary={summary} subjectId={personId} />
              <section>
                <h2 className="text-sm font-medium mb-2">{t("history.timeline")}</h2>
                <RecordTimeline
                  records={records}
                  openingId={openingId}
                  onOpen={(item) => {
                    setOpeningId(item.record_id);
                    setDetailSubjectId(personId);
                    setDetailId(item.record_id);
                  }}
                />
              </section>
              <ManageShares subjectId={personId} />
            </>
          )}
        </div>
      </div>
      <AddRecordModal
        open={addOpen}
        onOpenChange={setAddOpen}
        subjectId={personId}
        onCreated={load}
        maxFiles={summary?.max_files_per_record}
      />
      <HistoryConsentDialog
        open={consentOpen}
        onOpenChange={setConsentOpen}
        onAccepted={() => {
          load();
          setAddOpen(true);
        }}
      />
      {packetTab && (
        <DoctorPacketDialog
          open
          initialTab={packetTab}
          onOpenChange={(next) => {
            if (!next) setPacketTab(null);
          }}
          subjectId={personId}
          records={records}
          recordsTotal={recordsTotal}
          hasExportIdentity={Boolean(summary?.has_export_identity)}
          displayName={activePerson?.display_name}
          onCreated={load}
        />
      )}
      <RecordDetail
        open={Boolean(detailId)}
        recordId={detailId}
        subjectId={detailSubjectId}
        onOpenChange={(next) => {
          if (!next) {
            setDetailId(null);
            setOpeningId(null);
          }
        }}
        onLoaded={() => setOpeningId(null)}
        onChanged={load}
      />
    </div>
  );
};

export default HistoryPage;
